/**
 * F26 (Wave 2D / Decision 14) — admin notifications dry-run page.
 *
 * Route: `/admin/notifications/dry-run`
 * Permission: `notifications:dry_run` (super_admin only; gated by
 * ProtectedRoute in App.tsx).
 *
 * UX: paste a JSON `{ kind, recipientUserId, payload? }` envelope into the
 * Textarea + Submit → POST /admin/notifications/dry-run → render the
 * DryRunResult.channelDecisions table.
 *
 * The dispatcher's NotificationEvent union lives in apps/api (not shared via
 * @bravemove/types per Decision F1). The admin sends the kind+payload as a
 * stringly-typed envelope; full type-safety is deferred to runtime on the
 * server.
 */
import { useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Banner, Button, FormField, Textarea, useToast } from '@bravemove/ui-web';
import type { DryRunInput, DryRunResult } from '@bravemove/types';
import { adminNotificationsApi } from '../../../api/notifications';
import { translateApiError } from '../../../auth/errors';
import { PageHeader } from '../../../components/PageHeader';

interface ParsedInput {
  readonly ok: true;
  readonly value: DryRunInput;
}
interface ParseError {
  readonly ok: false;
  readonly error: string;
}

function parseInput(raw: string): ParsedInput | ParseError {
  if (!raw.trim()) {
    return { ok: false, error: 'Enter a JSON envelope.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      ok: false,
      error: `Invalid JSON: ${err instanceof Error ? err.message : 'parse error'}`,
    };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Envelope must be a JSON object.' };
  }
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.kind !== 'string' || !obj.kind.trim()) {
    return { ok: false, error: 'Missing or invalid "kind" (string).' };
  }
  if (typeof obj.recipientUserId !== 'string' || !obj.recipientUserId.trim()) {
    return { ok: false, error: 'Missing or invalid "recipientUserId" (string).' };
  }
  const payload = obj.payload;
  if (
    payload !== undefined &&
    (payload === null || typeof payload !== 'object' || Array.isArray(payload))
  ) {
    return { ok: false, error: '"payload" must be a JSON object if present.' };
  }
  return {
    ok: true,
    value: {
      kind: obj.kind.trim(),
      recipientUserId: obj.recipientUserId.trim(),
      payload: (payload as Record<string, unknown> | undefined) ?? {},
    },
  };
}

const SAMPLE = JSON.stringify(
  {
    kind: 'booking.accepted',
    recipientUserId: '00000000-0000-7000-8000-000000000001',
    payload: { bookingId: 'bk-example' },
  },
  null,
  2,
);

const DECISION_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'info'> = {
  would_send: 'success',
  suppressed_by_preference: 'warning',
  no_active_tokens: 'warning',
  skipped: 'neutral',
};

export function AdminNotificationsDryRunPage() {
  const { t } = useI18n();
  const toast = useToast();

  const [raw, setRaw] = useState<string>(SAMPLE);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<DryRunResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const parsed = useMemo(() => parseInput(raw), [raw]);

  const onSubmit = async () => {
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    setParseError(null);
    setSubmitError(null);
    setSubmitting(true);
    try {
      const r = await adminNotificationsApi.dryRun(parsed.value);
      setResult(r);
      toast.push({
        tone: 'success',
        title: t('admin:notifications.dryRun.toast.success', {
          defaultValue: 'Dry-run complete',
        }),
      });
    } catch (err) {
      setSubmitError(translateApiError(err, t));
      setResult(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-notifications-dry-run-page">
      <PageHeader
        title={t('admin:notifications.dryRun.title', { defaultValue: 'Notification dry-run' })}
        subtitle={t('admin:notifications.dryRun.subtitle', {
          defaultValue:
            'Preview which channels would dispatch a notification — no inbox row is written, no FCM/SES call is made.',
        })}
      />

      <Banner tone="info">
        {t('admin:notifications.dryRun.banner', {
          defaultValue: 'Super-admin only — this surface reveals recipient routing. Audit logged.',
        })}
      </Banner>

      <FormField
        label={t('admin:notifications.dryRun.eventLabel', {
          defaultValue: 'Event envelope (JSON)',
        })}
        helper={t('admin:notifications.dryRun.eventHint', {
          // ICU: the apostrophes quote the literal braces (not placeholders).
          defaultValue: "Shape: '{ kind, recipientUserId, payload? }'",
        })}
        error={parseError ?? undefined}
      >
        <Textarea
          rows={12}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          spellCheck={false}
          className="font-mono text-[12px]"
          data-testid="admin-notifications-dry-run-input"
        />
      </FormField>

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={submitting}
          disabled={submitting || !parsed.ok}
          onClick={() => void onSubmit()}
          data-testid="admin-notifications-dry-run-submit"
        >
          {t('admin:notifications.dryRun.submit', { defaultValue: 'Run dry-run' })}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setRaw(SAMPLE);
            setResult(null);
            setParseError(null);
            setSubmitError(null);
          }}
        >
          {t('admin:common.reset', { defaultValue: 'Reset' })}
        </Button>
      </div>

      {submitError ? <Banner tone="danger">{submitError}</Banner> : null}

      {result ? (
        <div
          className="rounded-xl border border-hairline bg-surface p-5 space-y-4"
          data-testid="admin-notifications-dry-run-result"
        >
          <div className="space-y-1">
            <h2 className="text-[15px] font-bold text-ink">
              {t('admin:notifications.dryRun.resultTitle', { defaultValue: 'Result' })}
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-3">
              <Badge tone="neutral" size="sm">
                {t('admin:notifications.dryRun.category', { defaultValue: 'Category' })}:{' '}
                {result.category}
              </Badge>
              <Badge tone="neutral" size="sm">
                {t('admin:notifications.dryRun.kind', { defaultValue: 'Kind' })}: {result.kind}
              </Badge>
              <Badge tone="neutral" size="sm">
                {t('admin:notifications.dryRun.activeTokens', {
                  defaultValue: 'Active tokens',
                })}
                : {result.activeDeviceTokenCount}
              </Badge>
            </div>
          </div>

          <div>
            <h3 className="text-[12px] font-semibold text-ink-2 uppercase tracking-wider mb-2">
              {t('admin:notifications.dryRun.recipient', { defaultValue: 'Recipient' })}
            </h3>
            <div className="space-y-0.5 text-[13px] text-ink">
              <div>
                <span className="text-ink-3">
                  {t('admin:notifications.dryRun.userId', { defaultValue: 'User ID' })}:{' '}
                </span>
                <span className="font-mono text-[11.5px]">{result.recipient.userId}</span>
              </div>
              {result.recipient.email ? (
                <div>
                  <span className="text-ink-3">
                    {t('admin:notifications.dryRun.email', { defaultValue: 'Email' })}:{' '}
                  </span>
                  <span className="font-mono text-[11.5px]">{result.recipient.email}</span>
                </div>
              ) : null}
              {result.recipient.displayName ? (
                <div>
                  <span className="text-ink-3">
                    {t('admin:notifications.dryRun.displayName', { defaultValue: 'Display name' })}
                    :{' '}
                  </span>
                  {result.recipient.displayName}
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <h3 className="text-[12px] font-semibold text-ink-2 uppercase tracking-wider mb-2">
              {t('admin:notifications.dryRun.channelDecisions', {
                defaultValue: 'Channel decisions',
              })}
            </h3>
            <table
              className="w-full text-[12.5px] border-collapse"
              data-testid="admin-notifications-dry-run-decisions-table"
            >
              <thead>
                <tr className="text-start text-ink-3">
                  <th className="text-start py-2 px-3 border-b border-hairline font-semibold">
                    {t('admin:notifications.dryRun.channel', { defaultValue: 'Channel' })}
                  </th>
                  <th className="text-start py-2 px-3 border-b border-hairline font-semibold">
                    {t('admin:notifications.dryRun.decision', { defaultValue: 'Decision' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(['in_app', 'push', 'email', 'whatsapp'] as const).map((channel) => {
                  const decision = result.channelDecisions[channel];
                  const tone = DECISION_TONE[decision] ?? 'neutral';
                  return (
                    <tr
                      key={channel}
                      className="border-b border-hairline last:border-b-0"
                      data-channel={channel}
                    >
                      <td className="py-2 px-3 font-mono text-[12px] text-ink">{channel}</td>
                      <td className="py-2 px-3">
                        <Badge tone={tone} size="sm">
                          {decision}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <details>
            <summary className="cursor-pointer text-[12px] font-semibold text-ink-2">
              {t('admin:notifications.dryRun.rawResult', { defaultValue: 'Raw result (JSON)' })}
            </summary>
            <pre className="mt-3 text-[11px] text-ink-2 overflow-x-auto font-mono">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}

export default AdminNotificationsDryRunPage;
